/* ============================================================
   game.ts — the game ENGINE.

   Responsible for: spawning falling items (several at once on the
   later levels), all three input methods (keyboard, mouse, touch),
   deciding right/wrong, scoring, lives, and level progression.

   It talks to the rest of the app through callbacks passed in via
   initGame(...). It never switches screens itself — ui.ts and
   main.ts do that. This keeps the engine focused.

   A "run" = one play session that can span several levels. Your
   score carries over from level to level; retrying a level rolls
   the score back to what it was when that level began.
   ============================================================ */

import { BIN_BY_ID, ITEMS, LEVELS, SCORING } from "./data";
import type { Bin, BinId, Level, WasteItem } from "./data";
import { Sound } from "./sound";

export interface HudData {
  score: number;
  lives: number;
  level: number;
  progress: number;
}

export interface LevelStats {
  score: number;
  correct: number;
  mistakes: number;
  bestStreak: number;
  perfect: boolean;
  level: number;
  levelIndex: number;
  runCorrect: number;
  runMistakes: number;
  runBestStreak: number;
}

export interface LevelEndData {
  won: boolean;
  isLastLevel: boolean;
  stats: LevelStats;
}

export interface WrongDropData {
  item: WasteItem;
  correctBin: Bin;
}

export interface GameCallbacks {
  onHud: (hud: HudData) => void;
  onLevelEnd: (data: LevelEndData) => void;
  onWrong: (data: WrongDropData, continueFn: () => void) => void;
  /** show "Level N" banner, then call done() */
  onLevelIntro: (level: Level, done: () => void) => void;
}

export interface GameRefs {
  playfield: HTMLElement;
  bins: HTMLElement;
  toast: HTMLElement;
  streak: HTMLElement;
}

interface FallingItem {
  el: HTMLElement;
  data: WasteItem;
  x: number;
  y: number;
  w: number;
  h: number;
  speed: number;
  spawnTime: number;
  resolved: boolean;
}

interface RunState {
  score: number;
  correct: number;
  mistakes: number;
  bestStreak: number;
}

interface LevelState {
  levelIndex: number;
  level: Level;
  lives: number;
  correct: number;
  mistakes: number;
  streak: number;
  bestStreak: number;
  paused: boolean;
  finished: boolean;
  levelStartScore: number;
}

// ---- DOM references (filled in initGame) ----
let playfield: HTMLElement;
let binsEl: HTMLElement;
let toastEl: HTMLElement;
let streakEl: HTMLElement;

// ---- Callbacks into the UI layer ----
let cb: GameCallbacks = {
  onHud: () => {},
  onLevelEnd: () => {},
  onWrong: () => {},
  onLevelIntro: (_lv, done) => done(),
};

// ---- Live state ----
let run: RunState | null = null; // survives across levels
let state: LevelState | null = null; // the current level's state
let currentBins: Bin[] = [];
let itemPool: WasteItem[] = [];
let items: FallingItem[] = []; // all items currently falling
let dragItem: FallingItem | null = null;
let rafId: number | null = null;
let lastFrame = 0;
let spawnWait = 0;

const ARROW_STEP = 28; // px per arrow-key press
const ITEM_W = 84;
const SPAWN_GAP = 1.1; // seconds between spawns when several fall at once
const DROP_ZONE = 0.74; // releasing a drag below 74% of the field commits the drop

/* ---------- Public: one-time setup ---------- */
export function initGame(refs: GameRefs, callbacks: Partial<GameCallbacks>): void {
  playfield = refs.playfield;
  binsEl = refs.bins;
  toastEl = refs.toast;
  streakEl = refs.streak;
  cb = { ...cb, ...callbacks };

  document.addEventListener("keydown", onKey);
  playfield.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
}

/* ---------- Public: runs & levels ---------- */
export function startRun(levelIndex = 0): void {
  run = { score: 0, correct: 0, mistakes: 0, bestStreak: 0 };
  startLevel(levelIndex);
}

export function nextLevel(): void {
  if (!state || !run) return startRun(0);
  startLevel(state.levelIndex + 1);
}

export function retryLevel(): void {
  if (!state || !run) return startRun(0);
  run.score = state.levelStartScore; // undo the failed attempt's points
  startLevel(state.levelIndex);
}

function startLevel(i: number): void {
  stopLoop();
  clearItems();
  const level = LEVELS[i];
  if (!level || !run) return;

  state = {
    levelIndex: i,
    level,
    lives: 3,
    correct: 0,
    mistakes: 0,
    streak: 0,
    bestStreak: 0,
    paused: true, // frozen until the level-intro banner finishes
    finished: false,
    levelStartScore: run.score,
  };

  currentBins = level.allowedBins.map((id) => BIN_BY_ID[id]);
  itemPool = ITEMS.filter((it) => level.allowedBins.includes(it.bin));

  buildBins();
  pushHud();
  spawnWait = 0;

  cb.onLevelIntro(level, () => {
    if (state && !state.finished) {
      state.paused = false;
      lastFrame = performance.now();
    }
  });

  lastFrame = performance.now();
  rafId = requestAnimationFrame(loop);
}

/* ---------- Pause / resume / quit ---------- */
export function pause(): void {
  if (state) state.paused = true;
}

export function resume(): void {
  if (state && state.paused && !state.finished) {
    state.paused = false;
    lastFrame = performance.now();
  }
}

export function quit(): void {
  stopLoop();
  clearItems();
  state = null;
  run = null;
}

export function isRunning(): boolean {
  return !!(state && !state.finished && !state.paused);
}

export function getScore(): number {
  return run ? run.score : 0;
}

/* ---------- Build the bin buttons for this level ---------- */
function buildBins(): void {
  binsEl.innerHTML = "";
  currentBins.forEach((bin) => {
    const el = document.createElement("button");
    el.className = "bin";
    el.style.background = bin.color;
    el.dataset.bin = bin.id;
    el.innerHTML = `<span class="bin-emoji">${bin.emoji}</span><span class="bin-label">${bin.label}</span>`;
    // Tap/click a bin to send the lowest falling item into it.
    el.addEventListener("click", () => {
      el.blur(); // so Space doesn't re-press this button later
      const it = activeItem();
      if (it && state && !state.paused && !state.finished) resolveDrop(it, bin.id);
    });
    binsEl.appendChild(el);
  });
}

/* ---------- Items ---------- */
function spawnItem(): void {
  if (!state) return;
  const data = itemPool[Math.floor(Math.random() * itemPool.length)];
  if (!data) return;
  const fieldW = playfield.clientWidth;
  const x = Math.random() * Math.max(1, fieldW - ITEM_W);

  const el = document.createElement("div");
  el.className = "item";
  el.innerHTML = `<span class="item-emoji">${data.emoji}</span><span class="item-name">${data.name}</span>`;
  playfield.appendChild(el);

  const it: FallingItem = {
    el,
    data,
    x,
    y: 0,
    w: ITEM_W,
    h: el.offsetHeight || 64,
    // ±10% speed variance keeps simultaneous items from clumping
    speed: state.level.speed * (0.9 + Math.random() * 0.2),
    spawnTime: performance.now(),
    resolved: false,
  };
  items.push(it);
  positionItem(it);
}

function removeItem(it: FallingItem): void {
  it.el.remove();
  items = items.filter((x) => x !== it);
  if (dragItem === it) dragItem = null;
}

function clearItems(): void {
  items.forEach((it) => it.el.remove());
  items = [];
  dragItem = null;
}

function positionItem(it: FallingItem): void {
  it.el.style.transform = `translate(${it.x}px, ${it.y}px)`;
}

// The item the keyboard / bin-click controls: the one nearest the bins.
function activeItem(): FallingItem | null {
  let best: FallingItem | null = null;
  for (const it of items) {
    if (it.resolved) continue;
    if (!best || it.y > best.y) best = it;
  }
  return best;
}

/* ---------- The main loop (runs ~60x/second) ---------- */
function loop(now: number): void {
  rafId = requestAnimationFrame(loop);
  const dt = Math.min((now - lastFrame) / 1000, 0.05);
  lastFrame = now;

  if (!state || state.paused || state.finished) return;

  // Spawn when there's room (later levels allow 2–3 at once)
  const stillNeeded = state.level.itemsToSort - state.correct;
  if (items.length < Math.min(state.level.concurrent, stillNeeded)) {
    spawnWait -= dt;
    if (spawnWait <= 0) {
      spawnItem();
      spawnWait = SPAWN_GAP;
    }
  }

  // Fall
  const maxYFor = (it: FallingItem) => playfield.clientHeight - it.h;
  for (const it of [...items]) {
    if (state.paused) break; // a wrong-drop modal opened mid-loop
    if (it === dragItem || it.resolved) continue;
    it.y += it.speed * dt;
    if (it.y >= maxYFor(it)) {
      // Landed: it goes into whatever bin column it's over.
      it.y = maxYFor(it);
      positionItem(it);
      resolveDrop(it, binIdAtX(it.x + it.w / 2));
    } else {
      positionItem(it);
    }
  }

  // When several items fall, glow the one the keyboard controls
  const act = activeItem();
  items.forEach((it) => it.el.classList.toggle("active", items.length > 1 && it === act));
}

function stopLoop(): void {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
}

/* ---------- Which bin sits under a given x? ---------- */
function binIdAtX(x: number): BinId {
  const slot = playfield.clientWidth / currentBins.length;
  let idx = Math.floor(x / slot);
  idx = Math.max(0, Math.min(currentBins.length - 1, idx));
  return currentBins[idx]!.id;
}

function highlightBinUnder(x: number): void {
  const id = binIdAtX(x);
  binsEl.querySelectorAll<HTMLElement>(".bin").forEach((b) => {
    b.classList.toggle("highlight", b.dataset.bin === id);
  });
}

function clearHighlight(): void {
  binsEl.querySelectorAll(".bin").forEach((b) => b.classList.remove("highlight"));
}

/* ---------- Resolve: an item goes into binId ---------- */
function resolveDrop(it: FallingItem, binId: BinId): void {
  if (!state || !run || state.finished || it.resolved) return;
  it.resolved = true;
  clearHighlight();

  const correct = it.data.bin === binId;

  if (correct) {
    const fast = performance.now() - it.spawnTime < SCORING.fastThresholdMs;

    // Base points depend on the category (per design doc)
    let pts: number = SCORING.correct;
    if (it.data.bin === "compost") pts = SCORING.compostCorrect;
    else if (it.data.bin === "hazardous") pts = SCORING.hazardousCorrect;
    if (fast) pts += SCORING.fastBonus;

    run.score += pts;
    state.correct += 1;
    state.streak += 1;
    state.bestStreak = Math.max(state.bestStreak, state.streak);

    // Streak bonuses
    let streakMsg = "";
    if (state.streak === 5) {
      run.score += SCORING.streak5Bonus;
      streakMsg = `🔥 5 Streak! +${SCORING.streak5Bonus}`;
      Sound.streak();
    } else if (state.streak === 10) {
      run.score += SCORING.streak10Bonus;
      streakMsg = `🔥 10 Streak! +${SCORING.streak10Bonus}`;
      Sound.streak();
    } else if (state.streak >= 2) {
      streakMsg = `🔥 ${state.streak} in a row`;
    }

    Sound.correct(fast);
    showToast(`+${pts}${fast ? " ⚡" : ""}`, it.data.fact);
    if (streakMsg) showStreak(streakMsg);

    removeItem(it);
    pushHud();

    if (state.correct >= state.level.itemsToSort) endLevel(true);
  } else {
    // Wrong bin: lose points + a life, break the streak, freeze and explain.
    run.score += SCORING.wrongBin;
    state.mistakes += 1;
    state.streak = 0;
    state.lives -= 1;
    pushHud();

    Sound.wrong();
    hurtFlash();

    const item = it.data;
    const correctBin = BIN_BY_ID[item.bin];
    state.paused = true; // freezes ALL falling items while the popup is open
    cb.onWrong({ item, correctBin }, () => {
      removeItem(it);
      if (state && state.lives <= 0) {
        endLevel(false);
      } else if (state) {
        state.paused = false;
        lastFrame = performance.now();
      }
    });
  }
}

function hurtFlash(): void {
  playfield.classList.remove("hurt");
  void playfield.offsetWidth; // restart the animation
  playfield.classList.add("hurt");
  setTimeout(() => playfield.classList.remove("hurt"), 500);
}

/* ---------- End of level (won or out of lives) ---------- */
function endLevel(won: boolean): void {
  if (!state || !run) return;
  state.finished = true;
  stopLoop();
  clearItems();

  if (won && state.mistakes === 0) run.score += SCORING.perfectLevelBonus;

  // Fold this level's results into the run totals
  run.correct += state.correct;
  run.mistakes += state.mistakes;
  run.bestStreak = Math.max(run.bestStreak, state.bestStreak);

  const isLastLevel = state.levelIndex >= LEVELS.length - 1;
  if (won) Sound.win();
  else Sound.lose();

  cb.onLevelEnd({
    won,
    isLastLevel,
    stats: {
      score: run.score,
      correct: state.correct,
      mistakes: state.mistakes,
      bestStreak: state.bestStreak,
      perfect: won && state.mistakes === 0,
      level: state.level.level,
      levelIndex: state.levelIndex,
      runCorrect: run.correct,
      runMistakes: run.mistakes,
      runBestStreak: run.bestStreak,
    },
  });
}

/* ---------- Input: keyboard ---------- */
function onKey(e: KeyboardEvent): void {
  if (!state || state.paused || state.finished) return;
  const it = activeItem();
  if (!it) return;
  if (e.key === "ArrowLeft") {
    it.x = Math.max(0, it.x - ARROW_STEP);
    positionItem(it);
    highlightBinUnder(it.x + it.w / 2);
  } else if (e.key === "ArrowRight") {
    it.x = Math.min(playfield.clientWidth - it.w, it.x + ARROW_STEP);
    positionItem(it);
    highlightBinUnder(it.x + it.w / 2);
  } else if (e.key === "ArrowDown" || e.key === " ") {
    e.preventDefault();
    resolveDrop(it, binIdAtX(it.x + it.w / 2)); // drop now
  }
}

/* ---------- Input: drag (mouse + touch via pointer events) ---------- */
function onPointerDown(e: PointerEvent): void {
  if (!state || state.paused || state.finished) return;
  const itemEl = (e.target as HTMLElement).closest<HTMLElement>(".item");
  if (!itemEl) return;
  const it = items.find((x) => x.el === itemEl);
  if (!it || it.resolved) return;
  dragItem = it;
  it.el.classList.add("dragging");
  if (itemEl.setPointerCapture) {
    try {
      itemEl.setPointerCapture(e.pointerId);
    } catch (_e) {
      /* ignore */
    }
  }
}

function onPointerMove(e: PointerEvent): void {
  if (!dragItem || !state || state.paused) return;
  const r = playfield.getBoundingClientRect();
  const it = dragItem;
  it.x = Math.max(0, Math.min(playfield.clientWidth - it.w, e.clientX - r.left - it.w / 2));
  it.y = Math.max(0, Math.min(playfield.clientHeight - it.h, e.clientY - r.top - it.h / 2));
  positionItem(it);
  highlightBinUnder(it.x + it.w / 2);
}

function onPointerUp(): void {
  if (!dragItem) return;
  const it = dragItem;
  dragItem = null;
  it.el.classList.remove("dragging");
  clearHighlight();
  // Only commit the drop if released near the bins —
  // released higher up, the item just keeps falling.
  if (state && !state.paused && it.y + it.h >= playfield.clientHeight * DROP_ZONE) {
    resolveDrop(it, binIdAtX(it.x + it.w / 2));
  }
}

/* ---------- Small UI helpers (toast & streak) ---------- */
let toastTimer: ReturnType<typeof setTimeout> | undefined;
function showToast(points: string, fact: string): void {
  toastEl.innerHTML = `${points}<span class="toast-fact">${fact}</span>`;
  toastEl.hidden = false;
  toastEl.style.animation = "none";
  void toastEl.offsetWidth; // restart animation
  toastEl.style.animation = "";
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastEl.hidden = true;
  }, 2200);
}

let streakTimer: ReturnType<typeof setTimeout> | undefined;
function showStreak(msg: string): void {
  streakEl.textContent = msg;
  streakEl.hidden = false;
  clearTimeout(streakTimer);
  streakTimer = setTimeout(() => {
    streakEl.hidden = true;
  }, 1400);
}

function pushHud(): void {
  if (!state || !run) return;
  cb.onHud({
    score: run.score,
    lives: state.lives,
    level: state.level.level,
    progress: Math.min(1, state.correct / state.level.itemsToSort),
  });
}
